import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '../../../ui/toast';
import { jobApplicantTwoD, searchCandidates } from '../../../utils/api_service/JobApplicants';
import { Applicant, ApplicantResponse, CandibankJobApplicantRequest, JobApplicantSearchRequest, Skill } from '../../../utils/types/JobOpenings';
import { FilterOptions } from '../Components/GraphFilterDrawer';
import { CandidateType, mockCandidates } from '../constants/mockData';

export const useCandidateExplorer = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [candidatesPerPage, setCandidatesPerPage] = useState(25);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [showPerPageDropdown, setShowPerPageDropdown] = useState(false);
  const perPageDropdownRef = useRef<HTMLDivElement>(null);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [selectedTransformedCandidates, setSelectedTransformedCandidates] =
      useState<CandidateType[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedCandidate, setSelectedCandidate] =
      useState<CandidateType | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showViewInviteListModal, setShowViewInviteListModal] =
      useState(false);
  const [candidateForInvite, setCandidateForInvite] =
      useState<CandidateType | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!showPerPageDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        perPageDropdownRef.current &&
          !perPageDropdownRef.current.contains(e.target as Node)
      ) {
        setShowPerPageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [showPerPageDropdown]);
  const [candidates, setCandidates] = useState<Applicant[]>([]);
  const [useSkillPercentage, setUseSkillPercentage] =
      useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [extractedSkill, setExtractedSkill] = useState<Skill[]>([]);
  const [extractedJobTitles, setExtractedJobTitles] = useState<Skill[]>([]);
  const [extractedKeywords, setExtractedKeywords] = useState<Skill[]>([]);
  const [highScoreCandidates, setHighScoreCandidates] = useState<number>(0);
  const [extractedBehavioralScore, setExtractedBehavioralScore] = useState<
      Skill[]
    >([]);

  const [searchObject, setSearchObject] =
      useState<CandibankJobApplicantRequest>({
        additionalSkills: {
          skillWeightage: false,
          similar: false,
          skills: [],
        },
        jobTitle: {
          similar: false,
          jobTitles: [],
        },
        keywords: {
          keywordWeightage: false,
          similar: false,
          keywords: [],
        },
        behavioralAttributes: {
          similar: false,
          behavioralAttributes: [],
        },
        gender: [],
        age: {
          minAge: 0,
          maxAge: 100,
          includeUnknown: true,
        },
        applicantStatus: [],
        candidateIds: [],
      });

  // Filter state
  const [filters, setFilters] = useState<FilterOptions>({
    skills: [],
    locations: [],
    experienceLevels: [],
    status: [],
    matchRange: [0, 100],
    availability: [],
    sortBy: 'matchPercentage',
    sortOrder: 'desc',
  });

  // Derived data for filters
  const availableSkills = useMemo(() => {
    const allSkills = mockCandidates.flatMap((c) => c.skills);
    return Array.from(new Set(allSkills)).sort();
  }, []);

  const availableLocations = useMemo(() => {
    const allLocations = mockCandidates.map((c) => c.location);
    return Array.from(new Set(allLocations)).sort();
  }, []);

  // Enhanced filtering and sorting logic
  const filteredCandidates = useMemo(() => {
    let filtered = mockCandidates.filter((candidate) => {
      // Search filter
      const matchesSearch =
          searchQuery === '' ||
          candidate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          candidate.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          candidate.location
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          candidate.skills.some((skill) =>
            skill.toLowerCase().includes(searchQuery.toLowerCase()),
          );

      // Skills filter
      const matchesSkills =
          filters.skills.length === 0 ||
          filters.skills.some((skill) => candidate.skills.includes(skill));

      // Location filter
      const matchesLocation =
          filters.locations.length === 0 ||
          filters.locations.includes(candidate.location);

      // Status filter
      const matchesStatus =
          filters.status.length === 0 ||
          filters.status.includes(candidate.status);

      // Experience filter (mock implementation)
      const matchesExperience =
          filters.experienceLevels.length === 0 ||
          filters.experienceLevels.some((level) => {
            const years = parseInt(candidate.experience);
            switch (level) {
              case '0-2':
                return years >= 0 && years <= 2;
              case '2-5':
                return years > 2 && years <= 5;
              case '5-8':
                return years > 5 && years <= 8;
              case '8+':
                return years > 8;
              default:
                return true;
            }
          });

      // Match score filter
      const matchesScore =
          candidate.matchPercentage >= filters.matchRange[0] &&
          candidate.matchPercentage <= filters.matchRange[1];

      return (
        matchesSearch &&
          matchesSkills &&
          matchesLocation &&
          matchesStatus &&
          matchesExperience &&
          matchesScore
      );
    });

    // Sorting
    filtered.sort((a, b) => {
      const { sortBy, sortOrder } = filters;
      let aVal: any, bVal: any;

      switch (sortBy) {
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        case 'matchPercentage':
          aVal = a.matchPercentage;
          bVal = b.matchPercentage;
          break;
        case 'experience':
          aVal = parseInt(a.experience);
          bVal = parseInt(b.experience);
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'location':
          aVal = a.location;
          bVal = b.location;
          break;
        default:
          aVal = a.matchPercentage;
          bVal = b.matchPercentage;
      }

      if (typeof aVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [searchQuery, filters]);

  // Pagination
  const totalPages = Math.ceil(totalCandidates / candidatesPerPage);
  // const totalPages = Math.ceil(filteredCandidates.length / candidatesPerPage);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-emerald-600 bg-emerald-50';
      case 'not-looking':
        return 'text-slate-600 bg-slate-100';
      case 'active':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Open to Opportunities':
        return 'Open to Opportunities';
      case 'not-looking':
        return 'Not looking';
      case 'Actively Looking':
        return 'Actively Looking';
      default:
        return 'Unknown';
    }
  };

  const getMatchColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-emerald-500';
    if (percentage >= 80) return 'bg-cyan-500';
    if (percentage >= 70) return 'bg-blue-500';
    return 'bg-slate-500';
  };

  // Handlers
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Re-read the pool so the list matches the bank after an upload batch settles.
      await searchApplicants();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFiltersChange = useCallback((newFilters: FilterOptions) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      skills: [],
      locations: [],
      experienceLevels: [],
      status: [],
      matchRange: [0, 100],
      availability: [],
      sortBy: 'matchPercentage',
      sortOrder: 'desc',
    });
    setCurrentPage(1);
  }, []);

  const handleCandidateSelect = (candidateId: string) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId],
    );
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === selectedTransformedCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(selectedTransformedCandidates.map((c) => c.id));
    }
  };

  const handleViewProfile = (candidate: CandidateType) => {
    setSelectedCandidate(candidate);
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
    setSelectedCandidate(null);
  };

  const handleOpenInviteModal = (candidate: CandidateType) => {
    setCandidateForInvite(candidate);
    setShowInviteModal(true);
  };

  const handleBulkInvite = () => {
    setCandidateForInvite(null); // Indicates bulk invite
    setShowInviteModal(true);
  };
    // Auto-hide bulk actions when no candidates are selected
  useEffect(() => {
    setShowBulkActions(selectedCandidates.length > 0);
  }, [selectedCandidates]);

  // Auto-save search and filters to localStorage
  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      if (pageNumber !== 1) {
        setPageNumber(1);
      } else {
        searchApplicants();
      }
    }, 1000);

    const searchData = { searchQuery, filters, viewMode };
    localStorage.setItem(
      'candidateExplorer_preferences',
      JSON.stringify(searchData),
    );

    return () => clearTimeout(debounceSearchTerm);
  }, [searchQuery, filters, candidatesPerPage, currentPage]);

  // Load saved preferences on mount
  useEffect(() => {
    const saved = localStorage.getItem('candidateExplorer_preferences');
    if (saved) {
      try {
        const {
          searchQuery: savedSearch,
          filters: savedFilters,
          viewMode: savedView,
        } = JSON.parse(saved);
        if (savedSearch) setSearchQuery(savedSearch);
        if (savedFilters) setFilters({ ...filters, ...savedFilters });
        if (savedView) setViewMode(savedView);
      } catch (error) {
        console.error('Failed to load saved preferences:', error);
      }
    }
  }, []);

  const searchApplicants = async () => {
    setIsLoading(true);
    try {
      const response = await searchCandidates(
        searchQuery,
        currentPage,
        candidatesPerPage,
        candidatesPerPage,
      );

      if (response.ok) {
        const responseData = await response.json();
        const transformApplicant = (candidate: any): CandidateType => {
          const address = candidate.contact_info?.address;
          const city = address?.city;
          const country = address?.country;

          const state = address?.state;
          const location =
              city && state ? `${city}, ${state}` : city || state || undefined;

          return {
            id: candidate.id,
            name: candidate.first_name + ' ' + candidate.last_name,
            educations: candidate.educations ?? [],
            skills: candidate.skills ?? [],
            score: candidate.score,
            avatar: candidate.base_64_encoded ?? '',
            work_experience: candidate.work_experience ?? [],
            status: candidate.job_seeking_status ?? '',
            experience: candidate.total_experience ?? 0,
            email: candidate.email ?? '',
            phone: candidate.contact_info?.phone || undefined,
            location,
            inviteLists: [],
            filename: candidate.filename || candidate.fileName || candidate.s3Filename || candidate.s3_filename || candidate.pdfUrl || candidate.pdf_url || candidate.file_path || candidate.filePath || candidate.cv_filename,
            pdfUrl: candidate.pdfUrl || candidate.pdf_url || candidate.url,
            documents: candidate.applicant_documents || candidate.documents || candidate.applicantDocuments || [],
          };
        };

        const transformedData: CandidateType[] =
            responseData.results.map(transformApplicant);

        setSelectedTransformedCandidates(transformedData);
        setTotalCandidates(responseData.total_count);
        setHighScoreCandidates(
          transformedData.filter((c) => c.score > 0.75).length,
        );
        setSearchObject((prev) => ({
          ...prev,
          candidateIds: responseData.results.map((c: any) => c.id),
        }));
      }
    } catch (e) {
      setSelectedTransformedCandidates([]);
      setCurrentPage(1);
    } finally {
      setIsLoading(false);
    }
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const updateStates = async () => {
      try {
        await fetchJobApplicants();
      } catch (error) {
        toast.error('Error fetching job applicants:', {
          position: 'bottom-center',
        });
      }
    };

    updateStates();
  }, [searchObject]);

  const fetchJobApplicants = useCallback(async () => {
    setLoading(true);
    try {
      const updatedSearchObject: JobApplicantSearchRequest = {
        additional_skills: {
          skill_weightage: useSkillPercentage,
          similar: searchObject.additionalSkills.similar,
          skills: searchObject.additionalSkills.skills,
        },
        job_title: {
          similar: searchObject.jobTitle.similar,
          job_titles: searchObject.jobTitle.jobTitles,
        },
        keywords: {
          keyword_weightage: searchObject.keywords.keywordWeightage,
          similar: searchObject.keywords.similar,
          keywords: searchObject.keywords.keywords,
        },
        behavioral_attributes: {
          similar: searchObject.behavioralAttributes.similar,
          behavioral_attributes:
              searchObject.behavioralAttributes.behavioralAttributes,
        },
        gender: searchObject.gender,
        age: {
          min_age: searchObject.age.minAge,
          max_age: searchObject.age.maxAge,
          include_unknown: searchObject.age.includeUnknown,
        },
        applicant_status: searchObject.applicantStatus,
        candidate_ids: searchObject.candidateIds,
      };
      const response = await jobApplicantTwoD(updatedSearchObject);
      const responseData = await response.json();
      const getInitials = (name: string) => {
        const nameParts = name.split(' ').filter(Boolean);
        const initials = nameParts
          .map((part) => part[0].toUpperCase())
          .join('');
        return initials[0] + initials[1];
      };
      const transformApplicant = (item: ApplicantResponse): Applicant => {
        return {
          userId: item.id,
          value: useSkillPercentage
            ? [
              item.skillPercentage || 0,
              item.experience,
              item.behaviorCount,
              item.keywordCount,
            ]
            : [
              item.skillCount,
              item.experience,
              item.behaviorCount,
              item.keywordCount,
            ],
          firstName: item.firstName !== null ? item.firstName : 'undefined',
          lastName: item.lastName !== null ? item.lastName : 'undefined',
          age: item.age,
          gender: item.gender,
          aiScore: item.aiScore,
          applicantStatus: item.applicantStatus,
          locationCity: item.locationCity,
          locationCountry: item.locationCountry,
          name:
              item.firstName && item.lastName !== null
                ? item.firstName + ' ' + item.lastName
                : 'undefined',
          skills: item.skills,
          matchedSkills: item.matchedSkills,
          behavioralAttributes: item.behavioralAttributes,
          matchedBehavior: item.matchedBehavior,
          behavior: item.behavior,
          keywords: item.keyword,
          email: item.email,
          symbol: item.base64Encoded
            ? `image://${item.base64Encoded}`
            : 'circle',
          label: {
            show: item.base64Encoded ? false : true,
            formatter: getInitials(
              item.firstName && item.lastName !== null
                ? item.firstName + ' ' + item.lastName
                : 'undefined',
            ),
            color: 'yellow',
            fontWeight: 'bold',
          },
        };
      };

      const transformedData: Applicant[] =
          responseData.data.map(transformApplicant);
      setCandidates(transformedData);

      if (responseData.data && Array.isArray(responseData.data)) {
        setSelectedTransformedCandidates((prev) =>
          prev.map((c) => {
            const item = responseData.data.find(
              (d: any) => d.id === c.id || d.userId === c.id,
            );
            if (item) {
              const fn =
                item.filename ||
                item.fileName ||
                item.s3Filename ||
                item.s3_filename ||
                item.pdfUrl ||
                item.pdf_url ||
                item.file_path ||
                item.filePath ||
                item.cv_filename;
              return {
                ...c,
                filename: fn || c.filename,
                pdfUrl: item.pdfUrl || item.pdf_url || item.url || c.pdfUrl,
                documents:
                  item.applicant_documents ||
                  item.documents ||
                  item.applicantDocuments ||
                  c.documents,
              };
            }
            return c;
          }),
        );
      }
    } catch (error) {
      toast.error('Error Loading Job Applicants', {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false); // ✅ always runs, even if error
    }
  }, [searchObject]);

  const getActiveFiltersCount = () => {
    return (
      filters.skills.length +
        filters.locations.length +
        filters.experienceLevels.length +
        filters.status.length +
        filters.availability.length +
        (filters.matchRange[0] > 0 || filters.matchRange[1] < 100 ? 1 : 0)
    );
  };
  const [showUseJdModal, setShowUseJdModal] = useState(false);

  const handleAutoFillSearch = useCallback((searchString: string) => {
    setSearchQuery(searchString);
    setCurrentPage(1);
  }, []);

  const handleAutoFillAnalytics = useCallback((extracted: any) => {
    const newSkills = (extracted.technicalSkills || []).map((s: string) => ({
      skill: s,
      weight: 1,
      weightPercent: 0,
    }));
    const newTitles = extracted.jobTitle
      ? [{ skill: extracted.jobTitle, weight: 1, weightPercent: 0 }]
      : [];
    const newBehaviors = (extracted.behavioralAttributes || []).map((s: string) => ({
      skill: s,
      weight: 1,
      weightPercent: 0,
    }));
    const newKeywords = [
      ...(extracted.certifications || []),
      ...(extracted.industryExperience || []),
    ].map((s: string) => ({ skill: s, weight: 1, weightPercent: 0 }));

    setExtractedSkill(newSkills);
    setExtractedJobTitles(newTitles);
    setExtractedBehavioralScore(newBehaviors);
    setExtractedKeywords(newKeywords);

    setShowAnalyticsDialog(true);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  const handleClearAnalytics = useCallback(() => {
    setExtractedSkill([]);
    setExtractedJobTitles([]);
    setExtractedBehavioralScore([]);
    setExtractedKeywords([]);
  }, []);

  return {
    perPageDropdownRef,
    setShowPerPageDropdown,
    showPerPageDropdown,
    candidatesPerPage,
    setCandidatesPerPage,
    totalCandidates,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    setViewMode,
    viewMode,
    handleRefresh,
    showAnalyticsDialog,
    setShowAnalyticsDialog,
    setExtractedBehavioralScore,
    extractedBehavioralScore,
    setExtractedKeywords,
    extractedKeywords,
    setExtractedJobTitles,
    extractedJobTitles,
    setExtractedSkill,
    extractedSkill,
    setLoading,
    loading,
    candidates,
    setSearchObject,
    searchObject,
    getMatchColor,
    getStatusText,
    getStatusColor,
    handleCloseProfile,
    showProfileModal,
    selectedCandidate,
    availableLocations,
    handleClearFilters,
    showFilters,
    setShowFilters,
    availableSkills,
    handleFiltersChange,
    filters,
    setShowViewInviteListModal,
    showViewInviteListModal,
    selectedCandidates,
    selectedTransformedCandidates,
    candidateForInvite,
    showInviteModal,
    setShowInviteModal,
    totalPages,
    currentPage,
    handleOpenInviteModal,
    handleViewProfile,
    handleSelectAll,
    handleCandidateSelect,
    isLoading,
    getActiveFiltersCount,
    highScoreCandidates,
    handleBulkInvite,
    setSelectedCandidates,
    isRefreshing,
    showBulkActions,
    showUseJdModal,
    setShowUseJdModal,
    handleAutoFillSearch,
    handleAutoFillAnalytics,
    handleClearSearch,
    handleClearAnalytics,
  };
};
